import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as WellnessActions from './wellness.actions';
import { API } from '@app/core/models';
import moment from 'moment';
import { Store, Action } from '@ngrx/store';
import { State } from '@app/store';
import { selectMenu } from './wellness.selectors';
import { ReportsHttpService } from '@app/core/async-services/http';
import { SecureDataService } from '@app/core/services';
import { filterTruthy } from '@app/core/utils/rxjs.util';

@Injectable()
export class Effects {
  initializeCompletionReportPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.initializeCompletionReportPage),
      switchMap(() =>
        this._reportsHttpService.getTeamWellnessChecksHotels().pipe(
          withLatestFrom(this._store.select(selectMenu)),
          switchMap(([response, menu]) => {
            if (response?.payload !== undefined) {
              const actions: Action<string>[] = [
                WellnessActions.getTeamWellnessCheckInHotelsSuccess({
                  hotels: response.payload.hotels,
                }),
              ];
              if (!menu && response.payload.hotels.length) {
                actions.push(
                  WellnessActions.setCompletionReportMenuSelection({
                    entId: response.payload.hotels[0].entId,
                    date: moment().format('YYYY-MM-DD'),
                  }),
                );
              } else if (menu) {
                actions.push(
                  WellnessActions.setCompletionReportMenuSelection({
                    entId: menu.entId,
                    date: menu.date,
                  }),
                );
              }
              return actions;
            } else {
              return throwError(() => ({
                message:
                  'Failed to load wellness check hotels - payload undefined',
                statusCode: 500,
              }));
            }
          }),
          catchError((response: API.Response) =>
            of(
              WellnessActions.getTeamWellnessCheckInHotelsFailure({
                message:
                  response?.message || 'Failed to load wellness check hotels',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  initializeTeamRisksPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.initializeTeamRisksPage),
      switchMap(() =>
        this._reportsHttpService.getTeamWellnessChecksHotels().pipe(
          withLatestFrom(this._store.select(selectMenu)),
          switchMap(([response, menu]) => {
            if (response?.payload !== undefined) {
              const actions: Action<string>[] = [
                WellnessActions.getTeamWellnessCheckInHotelsSuccess({
                  hotels: response.payload.hotels,
                }),
              ];
              if (!menu && response.payload.hotels.length) {
                actions.push(
                  WellnessActions.setTeamRisksMenuSelection({
                    entId: response.payload.hotels[0].entId,
                  }),
                );
              } else if (menu) {
                actions.push(
                  WellnessActions.setTeamRisksMenuSelection({
                    entId: menu.entId,
                  }),
                );
              }
              return actions;
            } else {
              return throwError(() => ({
                message:
                  'Failed to load wellness check hotels - payload undefined',
                statusCode: 500,
              }));
            }
          }),
          catchError((response: API.Response) =>
            of(
              WellnessActions.getTeamWellnessCheckInHotelsFailure({
                message:
                  response?.message || 'Failed to load wellness check hotels',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  setCompletionReportMenuSelection$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.setCompletionReportMenuSelection),
      map(({ entId, date }) =>
        WellnessActions.getTeamWellnessCheckInReport({ entId, date }),
      ),
    ),
  );

  setTeamRisksMenuSelection$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.setTeamRisksMenuSelection),
      map(({ entId }) =>
        WellnessActions.getTeamWellnessCheckInRisks({ entId }),
      ),
    ),
  );

  getTeamWellnessCheckInReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.getTeamWellnessCheckInReport),
      switchMap(({ entId, date }) =>
        this._reportsHttpService.getTeamWellnessCheckInReport(entId, date).pipe(
          map(({ payload }) => payload),
          filterTruthy(),
          map((payload) =>
            WellnessActions.getTeamWellnessCheckInReportSuccess({
              report: {
                ...payload,
                entId,
                date,
              },
            }),
          ),
          catchError((response: API.Response) =>
            of(
              WellnessActions.getTeamWellnessCheckInReportFailure({
                message:
                  response?.message ||
                  'Failed to load wellness check in report',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getTeamWellnessCheckInRisks$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WellnessActions.getTeamWellnessCheckInRisks),
      switchMap(({ entId }) =>
        this._secureDataService.getTeamWellnessCheckInRisks(entId).pipe(
          map(({ payload }) => payload?.risks),
          filterTruthy(),
          map((risks) => {
            return WellnessActions.getTeamWellnessCheckInRisksSuccess({
              risks,
            });
          }),
          catchError((response: API.Response) =>
            of(
              WellnessActions.getTeamWellnessCheckInRisksFailure({
                message:
                  response?.message || 'Failed to load wellness check in risks',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private _actions$: Actions,
    private _secureDataService: SecureDataService,
    private _reportsHttpService: ReportsHttpService,
    private _store: Store<State>,
  ) {}
}
